/**
 * useModuleGate — THE canonical module + flag gate hook (build contract §2.7).
 *
 * Every module-gated surface resolves through this one hook (or the pure
 * `resolveModuleGate` it wraps). Resolution order:
 *
 *   1. Workspace module off → false
 *   2. Module has no registered individual flag → true (uniform)
 *   3. Explicit personal flag row, and the module policy allows this user to
 *      override → personal flag wins
 *   4. Otherwise → module policy userDefault (auto-on vs opt-in)
 *
 * Reactive to: workspace module/policy changes (shared cache events), personal
 * flag changes (feature-flags event, incl. Realtime pushes from other devices),
 * and the user's role.
 *
 * Do NOT write new gates as `isModuleEnabled(...) && isFeatureEnabled(...)` or
 * as module-only hooks — those were the Pattern A/C defects removed 2026-07-07
 * (see audits/PLATFORM_PANELS_AND_VERSIONS.md §2.1).
 */

import { useEffect, useReducer, useState } from 'react';
import { FEATURE_FLAGS_UPDATED_EVENT } from './featureFlags';
import {
  getWorkspaceModules,
  resolveModuleGate,
  useWorkspaceModulePolicies,
  useWorkspaceModules,
  type ModulePolicy,
} from './workspaceModulesService';
import { getCurrentWorkspaceId } from './supabaseSync';
import { useUserStore } from '../stores/prioritization/userStore';
import { isAdminRole } from '../types/prioritization/roles';

/** True when the module-gated surface should render for the current user. */
export function useModuleGate(moduleKey: string): boolean {
  return useModuleGateResolver()(moduleKey);
}

/**
 * The same gate as a function of the module key, for a list whose entries each
 * name their own module (the Reports home registry). Same resolution as
 * useModuleGate — one gate, asked several times.
 */
export function useModuleGateResolver(): (moduleKey: string) => boolean {
  const features = useWorkspaceModules();
  const policies = useWorkspaceModulePolicies();
  const currentRole = useUserStore((s) => s.currentRole);
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const onFlagsChanged = () => bump();
    window.addEventListener(FEATURE_FLAGS_UPDATED_EVENT, onFlagsChanged);
    return () => window.removeEventListener(FEATURE_FLAGS_UPDATED_EVENT, onFlagsChanged);
  }, []);
  const isAdmin = currentRole ? isAdminRole(currentRole) : false;
  return (moduleKey: string) => resolveModuleGate(features, policies, moduleKey, isAdmin);
}

/**
 * useModuleGateForWorkspace — the canonical gate resolved against an EXPLICIT
 * workspace: the workspace the rendered record belongs to, not the session's
 * current workspace.
 *
 * Why this exists (defect, 2026-08-21): `useModuleGate` resolves against
 * `getCurrentWorkspaceId()`, which follows the session (demo mode, active
 * tenant). A record-scoped surface — e.g. a Plan of Record viewed by URL —
 * can belong to a different workspace than the session's, so a module the
 * record's workspace enabled resolved OFF and the surface silently fell back
 * to legacy. Record-scoped surfaces must gate on the record's workspace.
 *
 * Falls back to the current-workspace resolution while the target workspace's
 * modules are loading, when no workspaceId is provided, or when the target IS
 * the current workspace (keeping the reactive cache path).
 */
export function useModuleGateForWorkspace(
  workspaceId: string | null | undefined,
  moduleKey: string
): boolean {
  const currentGate = useModuleGate(moduleKey);
  const currentRole = useUserStore((s) => s.currentRole);
  const [target, setTarget] = useState<{
    workspaceId: string;
    features: Record<string, boolean>;
    policies: Record<string, ModulePolicy>;
  } | null>(null);

  useEffect(() => {
    if (!workspaceId || workspaceId === getCurrentWorkspaceId()) {
      setTarget(null);
      return;
    }
    let cancelled = false;
    void getWorkspaceModules(workspaceId).then(({ data, policies, error }) => {
      if (cancelled || error) return;
      setTarget({ workspaceId, features: data, policies });
    });
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (!workspaceId || workspaceId === getCurrentWorkspaceId() || target?.workspaceId !== workspaceId) {
    return currentGate;
  }
  const isAdmin = currentRole ? isAdminRole(currentRole) : false;
  return resolveModuleGate(target.features, target.policies, moduleKey, isAdmin);
}
